import { useEffect, useRef, useState } from "react";
import { X, Loader } from "lucide-react";

/*
 * Fullscreen media viewer for images and videos.
 * - Images: pinch-zoom via native CSS; tap outside to close.
 * - Videos: autoplay with native controls, playsinline for iOS.
 * - Backdrop click or close button to dismiss.
 */
export default function FullscreenMediaViewer({ open, mediaType, src, poster, onClose }) {
  const [loaded, setLoaded] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setLoaded(false);
      return;
    }
    // Lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="fullscreen-viewer"
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
      onClick={onClose}
    >
      {!loaded && (
        <Loader size={28} className="text-white/60 animate-spin absolute" />
      )}

      <button
        data-testid="fullscreen-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        className="absolute top-[max(env(safe-area-inset-top),1rem)] right-4 z-10 w-10 h-10 rounded-full bg-white/15 backdrop-blur-md text-white flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Cerrar"
      >
        <X size={22} />
      </button>

      {mediaType === "image" && (
        <img
          data-testid="fullscreen-image"
          src={src}
          alt=""
          onLoad={() => setLoaded(true)}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full w-auto h-auto object-contain select-none"
          style={{ touchAction: "pinch-zoom" }}
          draggable={false}
        />
      )}

      {mediaType === "video" && (
        <video
          data-testid="fullscreen-video"
          ref={videoRef}
          src={src}
          poster={poster || undefined}
          controls
          autoPlay
          playsInline
          preload="metadata"
          onLoadedData={() => setLoaded(true)}
          onCanPlay={() => setLoaded(true)}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full w-auto h-auto bg-black"
        />
      )}
    </div>
  );
}
